import React from 'react';
import Modal from 'antd/lib/modal/Modal';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Radio from 'antd/lib/radio';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { useLocalStorage } from 'src/domain/useLocalStorage';
import { useAuth } from 'src/domain/auth/useAuth';

export interface IDownloadBillsXlsxFormData {
    applyFilters: boolean;
    columns: 'all' | 'current';
}

interface Props {
    onClose: () => void;
    onSubmit: (data: IDownloadBillsXlsxFormData) => void;
}

const STORAGE_KEY = 'customer-utility-bill-xlsx-download-options';

const DEFAULT_FORM_VALUE: IDownloadBillsXlsxFormData = {
    applyFilters: true,
    columns: 'all',
};

export const DownloadBillsXlsxModal: React.FC<Props> = ({ onClose, onSubmit }) => {
    const [form] = Form.useForm<IDownloadBillsXlsxFormData>();
    const auth = useAuth()!;
    const [options, setOptions] = useLocalStorage<IDownloadBillsXlsxFormData>(`${STORAGE_KEY}-${auth.user?.user_id}`, DEFAULT_FORM_VALUE);

    const handleConfirm = (formData: IDownloadBillsXlsxFormData) => {
        setOptions({ ...options, ...formData });
        onSubmit(formData);
    };

    return (
        <Modal
            open
            title="Download XLSX"
            destroyOnHidden
            closeIcon
            onCancel={onClose}
            styles={{
                footer: {
                    borderTop: 'none',
                    padding: '0 24px 24px 24px',
                },
            }}
            footer={[
                <Button key="modal-cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="download-xlsx-confirm" type="primary" onClick={form.submit}>
                    Download
                </Button>,
            ]}
        >
            <Form
                form={form}
                name="download-bills-xlsx-form"
                preserve={false}
                layout="vertical"
                onFinish={handleConfirm}
                style={{ marginTop: '12px' }}
                initialValues={{ ...DEFAULT_FORM_VALUE, ...options }}
            >
                <Space direction="vertical" size={0} style={{ marginBottom: '24px' }}>
                    <Typography.Text strong style={{ fontSize: '16px' }}>
                        XLSX Download Options
                    </Typography.Text>
                    <Typography.Text>Options will be set as default</Typography.Text>
                </Space>

                <Form.Item name="applyFilters">
                    <Radio.Group>
                        <Space direction="vertical" size={0}>
                            <Typography.Text strong>Filters</Typography.Text>
                            <Radio value={true}>
                                <Typography.Text strong>Apply Filters</Typography.Text>
                            </Radio>
                            <Typography.Text type="secondary" style={{ marginLeft: '24px' }}>
                                Download will only include bills currently displayed
                            </Typography.Text>

                            <Radio value={false}>
                                <Typography.Text strong>Download all Customer Utility Bills</Typography.Text>
                            </Radio>
                            <Typography.Text type="secondary" style={{ marginLeft: '24px' }}>
                                Current filters and search will not be applied to XLSX
                            </Typography.Text>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="columns">
                    <Radio.Group>
                        <Space direction="vertical" size={0}>
                            <Typography.Text strong>Columns to Download</Typography.Text>
                            <Radio value="all">
                                <Typography.Text strong>Download All Columns</Typography.Text>
                            </Radio>
                            <Typography.Text type="secondary" style={{ marginLeft: '24px' }}>
                                Download every Customer Utility Bill column
                            </Typography.Text>

                            <Radio value="current">
                                <Typography.Text strong>Download Current View</Typography.Text>
                            </Radio>
                            <Typography.Text type="secondary" style={{ marginLeft: '24px' }}>
                                Download columns shown in DRMS UI
                            </Typography.Text>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
};
